/**
 * gut-archetype-quiz.js
 * Re:your gut — Gut Archetype Quiz
 * Upload to: Shopify Admin > Online Store > Code Editor > assets/
 *
 * This file is loaded by gut-archetype-quiz.liquid via:
 *   <script src="{{ 'gut-archetype-quiz.js' | asset_url }}" defer></script>
 *
 * It reads per-instance config from window.RYGQuizInstances[sectionId]
 * so multiple quiz sections on one page never collide.
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════
     QUIZ DATA — questions & archetype definitions
  ═══════════════════════════════════════════════════════════════════ */

  var STEP1 = {
    title: 'What are you experiencing most often?',
    hint: 'Select everything that resonates — even if it comes and goes.',
    type: 'multi',
    options: [
      { text: 'Bloating after meals',                    scores: { fermenter: 2, overloaded: 1 } },
      { text: 'Visible abdominal distention',            scores: { fermenter: 2 } },
      { text: 'Excess gas',                              scores: { fermenter: 2, overloaded: 1 } },
      { text: 'Feeling full quickly',                    scores: { stalled: 2 } },
      { text: 'Sluggish digestion',                      scores: { stalled: 2, overloaded: 1 } },
      { text: 'Constipation',                            scores: { fermenter: 1, stalled: 2 } },
      { text: 'Loose stools',                            scores: { reactive: 2 } },
      { text: 'Food sensitivities',                      scores: { reactive: 2, overloaded: 1 } },
      { text: 'Burning or discomfort after eating',      scores: { overloaded: 1, reactive: 1 } },
      { text: 'Brain fog',                               scores: { overloaded: 2, rebuilder: 1 } },
      { text: 'Fatigue after meals',                     scores: { overloaded: 2 } },
      { text: 'Irregular digestion',                     scores: { stalled: 1, reactive: 1 } },
      { text: 'Pressure in the upper abdomen',           scores: { fermenter: 2 } },
      { text: 'Symptoms that worsen throughout the day', scores: { fermenter: 1, overloaded: 1 } },
      { text: 'Symptoms triggered by stress',            scores: { reactive: 2, rebuilder: 1 } }
    ]
  };

  var STEP2 = {
    title: 'When do you notice it most?',
    hint: 'Select all that apply.',
    type: 'multi',
    options: [
      { text: 'After eating',                scores: { fermenter: 1, stalled: 1 } },
      { text: 'At night',                    scores: { fermenter: 1, stalled: 1 } },
      { text: 'After carbohydrates or sugar', scores: { fermenter: 2 } },
      { text: 'During stress',               scores: { reactive: 2 } },
      { text: 'While traveling',             scores: { reactive: 1, rebuilder: 1 } },
      { text: 'Around hormonal shifts',      scores: { reactive: 2 } },
      { text: 'After antibiotics',           scores: { rebuilder: 2, reactive: 1 } },
      { text: 'After alcohol',               scores: { overloaded: 2 } },
      { text: 'Randomly throughout the day', scores: { reactive: 1 } },
      { text: 'Almost all the time',         scores: { overloaded: 1, rebuilder: 1 } }
    ]
  };

  /* Step 3 options reference asset keys — resolved per-instance at render time */
  var STEP3_OPTIONS = [
    { text: 'Reduce bloating',               assetKey: 'reduce_bloating',           scores: { fermenter: 2 } },
    { text: 'Feel comfortable after eating', assetKey: 'feel_comfortable',           scores: { stalled: 2 } },
    { text: 'Improve regularity',            assetKey: 'improve_regularity',         scores: { stalled: 2 } },
    { text: 'Understand what\'s causing this', assetKey: 'understand_whats_going_on', scores: { rebuilder: 2 } },
    { text: 'Restore balance naturally',     assetKey: 'restore_balance',            scores: { rebuilder: 2 } },
    { text: 'Support long-term gut health',  assetKey: 'support_long_term',          scores: { rebuilder: 2 } },
    { text: 'Reduce digestive discomfort',   assetKey: 'reduce_discomfort',          scores: { overloaded: 2 } },
    { text: 'Feel more confident socially',  assetKey: 'feel_confident',             scores: { fermenter: 1, reactive: 1 } },
    { text: 'Improve energy and focus',      assetKey: 'improve_energy',             scores: { overloaded: 2, rebuilder: 1 } }
  ];

  /* ═══════════════════════════════════════════════════════════════════
     ARCHETYPES
  ═══════════════════════════════════════════════════════════════════ */

  function buildArchetypes(settings) {
    var b = settings.blog_url     || 'https://regardingyourgut.com/blog/';
    var a = settings.atrantil_url || 'https://regardingyourgut.com/products/atrantil';
    var g = settings.guide_url    || 'https://regardingyourgut.com/';
    var p = settings.podcast_url  || 'https://regardingyourgut.com/';

    return {
      fermenter: {
        theme: 'rq-theme-fermenter',
        name: 'The Fermenter',
        eyebrow: 'Your Gut Archetype',
        pattern: 'Gas · Bloating · Pressure · Distention',
        desc: 'Your answers suggest patterns commonly associated with microbial fermentation and digestive gas buildup in the gut. This is one of the most common digestive patterns — and one of the most misunderstood. The discomfort is real, and it\'s a signal worth paying attention to.',
        illustrationLabel: 'Paula\'s Illustration — The Fermenter',
        signs: [
          'Bloating after meals',
          'Feeling "pregnant" by the evening',
          'Excess gas and pressure',
          'Symptoms worse after carbohydrates or fiber',
          'Constipation or slowed transit'
        ],
        learn: [
          { text: 'What causes gas and bloating?', href: b },
          { text: 'Understanding fermentation in the gut', href: b },
          { text: 'Methane and digestive slowing', href: b },
          { text: 'Why bloating is a signal, not "normal"', href: b }
        ],
        resources: [
          { text: 'Atrantil', icon: '🌿', href: a },
          { text: 'The 30-Day Re:Set Guide', icon: '📋', href: g },
          { text: 'Podcast — Bloating & Methane', icon: '🎙️', href: p },
          { text: 'Blog: Gas and bloating are a signal', icon: '📖', href: b }
        ]
      },
      stalled: {
        theme: 'rq-theme-stalled',
        name: 'The Stalled Gut',
        eyebrow: 'Your Gut Archetype',
        pattern: 'Slow Movement · Fullness · Heaviness',
        desc: 'Your answers suggest your digestive system may need support with movement and flow. When digestion slows down, everything feels heavier — food sits longer, energy dips, and discomfort builds. The goal is restoring natural rhythm, not forcing it.',
        illustrationLabel: 'Paula\'s Illustration — The Stalled Gut',
        signs: [
          'Feeling full quickly after eating',
          'Food sitting heavily in your stomach',
          'Sluggish digestion throughout the day',
          'Constipation or infrequent bowel movements',
          'Symptoms that get worse as the day goes on'
        ],
        learn: [
          { text: 'The importance of digestive movement', href: b },
          { text: 'Why gut transit matters', href: b },
          { text: 'The connection between motility and bloating', href: b }
        ],
        resources: [
          { text: 'Atrantil', icon: '🌿', href: a },
          { text: 'Gut Movement & Motility Guide', icon: '📋', href: g },
          { text: 'Daily Rhythm & Digestion', icon: '🕐', href: g }
        ]
      },
      reactive: {
        theme: 'rq-theme-reactive',
        name: 'The Reactive Gut',
        eyebrow: 'Your Gut Archetype',
        pattern: 'Sensitivity · Inconsistency · Fluctuation',
        desc: 'Your symptoms may reflect a digestive system that reacts strongly to food, stress, or lifestyle changes. The unpredictability is part of the pattern — good days and bad days, with no clear single cause. The gut-brain connection plays a significant role here.',
        illustrationLabel: 'Paula\'s Illustration — The Reactive Gut',
        signs: [
          'Symptoms that seem unpredictable',
          'Strong food sensitivities',
          'Stress-related digestive flares',
          'Fluctuating good days and bad days',
          'Discomfort without an obvious trigger'
        ],
        learn: [
          { text: 'The gut-brain connection', href: b },
          { text: 'Stress and digestion', href: b },
          { text: 'Understanding gut sensitivity', href: b }
        ],
        resources: [
          { text: 'Nervous System & Gut Health', icon: '🧠', href: g },
          { text: 'Foundational Gut Education', icon: '📚', href: g },
          { text: 'Podcast Resources', icon: '🎙️', href: p }
        ]
      },
      overloaded: {
        theme: 'rq-theme-overloaded',
        name: 'The Overloaded Gut',
        eyebrow: 'Your Gut Archetype',
        pattern: 'Too Many Symptoms · Feeling Off · Overwhelmed',
        desc: 'Your answers suggest your digestive system may be overwhelmed and struggling to maintain balance. When too many things are happening at once — fatigue, fog, bloating, discomfort — it often means the system needs foundational support, not a targeted fix.',
        illustrationLabel: 'Paula\'s Illustration — The Overloaded Gut',
        signs: [
          'Multiple digestive symptoms at once',
          'Fatigue after meals',
          'Brain fog that follows eating',
          'Bloating and general discomfort',
          'A persistent feeling of being "off"'
        ],
        learn: [
          { text: 'Inflammation and the gut', href: b },
          { text: 'The role of polyphenols', href: b },
          { text: 'Restoring gut resilience', href: b }
        ],
        resources: [
          { text: 'Atrantil', icon: '🌿', href: a },
          { text: 'Foundational Gut Health Blog', icon: '📖', href: b },
          { text: 'Beginner Protocol Resources', icon: '📋', href: g }
        ]
      },
      rebuilder: {
        theme: 'rq-theme-rebuilder',
        name: 'The Rebuilder',
        eyebrow: 'Your Gut Archetype',
        pattern: 'Restoration · Resilience · Root Cause',
        desc: 'You may be in a rebuilding phase — after long-term digestive struggles, medications, stress, or repeated approaches that only managed symptoms temporarily. This archetype is about going deeper, not just feeling better for a few weeks.',
        illustrationLabel: 'Paula\'s Illustration — The Rebuilder',
        signs: [
          'Long digestive history with recurring issues',
          'Frustration with temporary fixes',
          'Symptoms that keep returning',
          'A desire to understand the root cause',
          'Feeling disconnected from your own body'
        ],
        learn: [
          { text: 'Gut restoration and resilience', href: b },
          { text: 'Supporting long-term digestive health', href: b },
          { text: 'Creating a better foundation', href: b }
        ],
        resources: [
          { text: 'Atrantil', icon: '🌿', href: a },
          { text: 'Dr. Ken Brown Podcast', icon: '🎙️', href: p },
          { text: 'Long-Form Educational Content', icon: '📚', href: g },
          { text: 'Beginner Gut Health Roadmap', icon: '🗺️', href: g }
        ]
      }
    };
  }

  /* ═══════════════════════════════════════════════════════════════════
     INSTANCE FACTORY
     One instance per section.id — state is fully isolated.
  ═══════════════════════════════════════════════════════════════════ */

  function RYGQuiz(config) {
    var SID      = config.sectionId;
    var assets   = config.assets;
    var settings = config.settings;
    var ARCHETYPES = buildArchetypes(settings);

    /* State */
    var state = { step: 1, s1: [], s2: [], s3: null };

    /* DOM helpers scoped to this instance */
    function el(suffix) { return document.getElementById(suffix + '-' + SID); }
    function root()     { return document.getElementById('ryg-quiz-' + SID); }

    /* ── Step 3 options with resolved asset URLs ── */
    var step3Options = STEP3_OPTIONS.map(function (opt) {
      return { text: opt.text, imgUrl: assets[opt.assetKey] || '', scores: opt.scores };
    });

    var STEP3 = {
      title: 'What\'s your main goal right now?',
      hint: 'Pick the one that feels most true.',
      type: 'single',
      options: step3Options
    };

    /* ── Render a quiz step ── */
    function render() {
      var stepData = state.step === 1 ? STEP1 : state.step === 2 ? STEP2 : STEP3;
      var selected = state.step === 1 ? state.s1 : state.step === 2 ? state.s2 : (state.s3 !== null ? [state.s3] : []);
      var pct = ((state.step - 1) / 3) * 100;

      var html = '<div class="rq-step-panel">';

      /* Step header */
      html += '<div class="rq-step-header">'
        + '<span class="rq-step-num">Step ' + state.step + ' of 3</span>'
        + '<div class="rq-track"><div class="rq-fill" style="width:' + pct + '%"></div></div>'
        + '</div>';

      html += '<div class="rq-step-title">' + stepData.title + '</div>';
      html += '<div class="rq-step-hint">' + stepData.hint + '</div>';

      /* Steps 1 & 2 — chip grid */
      if (state.step <= 2) {
        html += '<div class="rq-chip-grid">';
        stepData.options.forEach(function (opt, i) {
          var isSel = selected.indexOf(i) > -1;
          html += '<button class="rq-chip' + (isSel ? ' selected' : '') + '" '
            + 'data-rq-step="' + state.step + '" data-rq-idx="' + i + '" type="button">'
            + '<span class="rq-chip-check">'
            + '<svg width="9" height="9" viewBox="0 0 10 10" fill="none">'
            + '<polyline points="1.5,5 4,7.5 8.5,2" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>'
            + '</svg></span>'
            + opt.text + '</button>';
        });
        html += '</div>';
      }

      /* Step 3 — goal cards with SVG images */
      if (state.step === 3) {
        html += '<div class="rq-goal-grid">';
        step3Options.forEach(function (opt, i) {
          var isSel = state.s3 === i;
          html += '<button class="rq-goal-card' + (isSel ? ' selected' : '') + '" '
            + 'data-rq-step="3" data-rq-idx="' + i + '" type="button">'
            + '<img src="' + opt.imgUrl + '" alt="' + opt.text + '" class="rq-goal-img" width="72" height="72" loading="lazy">'
            + '<span class="rq-goal-text">' + opt.text + '</span>'
            + '</button>';
        });
        html += '</div>';
      }

      /* Nav */
      html += '<div class="rq-nav">';
      if (state.step > 1) {
        html += '<button class="rq-btn-back" id="rqBack-' + SID + '" type="button">'
          + '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg> Back</button>';
      } else {
        html += '<span></span>';
      }

      var canAdvance = state.step === 1 ? state.s1.length > 0
        : state.step === 2 ? state.s2.length > 0
        : state.s3 !== null;

      html += '<button class="rq-btn-next" id="rqNext-' + SID + '" type="button"'
        + (canAdvance ? '' : ' disabled') + '>'
        + (state.step === 3 ? 'Find My Archetype →' : 'Continue →')
        + '</button>';
      html += '</div></div>'; /* nav + step-panel */

      var container = el('rqContainer');
      container.innerHTML = html;
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ── Select option in-place (no full re-render) ── */
    function selectOption(step, idx) {
      if (step === 1) {
        var p1 = state.s1.indexOf(idx);
        if (p1 > -1) state.s1.splice(p1, 1); else state.s1.push(idx);
        root().querySelectorAll('[data-rq-step="1"][data-rq-idx="' + idx + '"]').forEach(function (b) {
          b.classList.toggle('selected', state.s1.indexOf(idx) > -1);
        });
      } else if (step === 2) {
        var p2 = state.s2.indexOf(idx);
        if (p2 > -1) state.s2.splice(p2, 1); else state.s2.push(idx);
        root().querySelectorAll('[data-rq-step="2"][data-rq-idx="' + idx + '"]').forEach(function (b) {
          b.classList.toggle('selected', state.s2.indexOf(idx) > -1);
        });
      } else {
        /* Single select — deselect previous */
        var prev = state.s3;
        state.s3 = (prev === idx) ? null : idx;
        if (prev !== null) {
          var old = root().querySelector('[data-rq-step="3"][data-rq-idx="' + prev + '"]');
          if (old) old.classList.remove('selected');
        }
        var cur = root().querySelector('[data-rq-step="3"][data-rq-idx="' + idx + '"]');
        if (cur) cur.classList.toggle('selected', state.s3 === idx);
      }

      /* Update Continue button */
      var canAdvance = state.step === 1 ? state.s1.length > 0
        : state.step === 2 ? state.s2.length > 0
        : state.s3 !== null;
      var nextBtn = el('rqNext');
      if (nextBtn) nextBtn.disabled = !canAdvance;
    }

    /* ── Score calculation ── */
    function calcScores() {
      var scores = { fermenter: 0, stalled: 0, reactive: 0, overloaded: 0, rebuilder: 0 };

      function addScores(opts, idxs) {
        idxs.forEach(function (i) {
          var s = opts[i].scores;
          Object.keys(s).forEach(function (k) { scores[k] += s[k]; });
        });
      }

      addScores(STEP1.options, state.s1);
      addScores(STEP2.options, state.s2);
      if (state.s3 !== null) addScores(step3Options, [state.s3]);
      return scores;
    }

    /* ── Tie-breaking ── */
    function pickArchetype(scores) {
      var max = 0;
      Object.keys(scores).forEach(function (k) { if (scores[k] > max) max = scores[k]; });
      var tied = Object.keys(scores).filter(function (k) { return scores[k] === max; });
      if (tied.length === 1) return tied[0];

      /* Symptom-based tie-breaking */
      var s1Texts = state.s1.map(function (i) { return STEP1.options[i].text; });
      var hasGasBloat     = s1Texts.some(function (t) { return /bloating|gas|distention|pressure/i.test(t); });
      var hasFullSluggish = s1Texts.some(function (t) { return /full quickly|sluggish|constipation/i.test(t); });
      var hasFoodStress   = s1Texts.some(function (t) { return /sensitivit|stress|loose/i.test(t); });
      var hasBrainFog     = s1Texts.some(function (t) { return /brain fog|fatigue/i.test(t); });

      if (tied.indexOf('fermenter')  > -1 && hasGasBloat)     return 'fermenter';
      if (tied.indexOf('stalled')    > -1 && hasFullSluggish)  return 'stalled';
      if (tied.indexOf('reactive')   > -1 && hasFoodStress)    return 'reactive';
      if (tied.indexOf('overloaded') > -1 && hasBrainFog)      return 'overloaded';
      if (tied.indexOf('rebuilder')  > -1)                      return 'rebuilder';
      return tied[0];
    }

    /* ── Build & show results ── */
    function showResults() {
      var scores  = calcScores();
      var winner  = pickArchetype(scores);
      var arch    = ARCHETYPES[winner];
      var atUrl   = settings.atrantil_url || 'https://regardingyourgut.com/products/atrantil';

      /* Hide quiz, show results */
      el('rqQuiz').style.display    = 'none';
      el('rqResults').style.display = 'block';
      el('rqResults').scrollIntoView({ behavior: 'smooth' });

      /* Apply archetype theme class */
      el('rqResults').className = 'rq-results ' + arch.theme;

      /* Populate static elements */
      el('rqEyebrow').textContent          = arch.eyebrow;
      el('rqName').textContent             = arch.name;
      el('rqPattern').textContent          = arch.pattern;
      el('rqDesc').textContent             = arch.desc;
      el('rqIllustrationLabel').textContent = arch.illustrationLabel;

      /* CTA row */
      el('rqCtaRow').innerHTML =
        '<a href="' + atUrl + '" target="_blank" class="rq-btn-primary">Explore Your Resources</a>'
        + '<button class="rq-btn-outline" id="rqRetakeTop-' + SID + '" type="button">Retake Quiz</button>';
      var retakeTopBtn = el('rqRetakeTop');
      if (retakeTopBtn) retakeTopBtn.addEventListener('click', retake);

      /* Signs + Learn two-col */
      var signsHTML = arch.signs.map(function (s) {
        return '<li><span class="rq-li-dot"></span>' + s + '</li>';
      }).join('');
      var learnHTML = arch.learn.map(function (l) {
        return '<a href="' + l.href + '" target="_blank">' + l.text + '</a>';
      }).join('');
      el('rqTwoCol').innerHTML =
        '<div class="rq-card"><div class="rq-card-label">Common Signs</div><ul>' + signsHTML + '</ul></div>'
        + '<div class="rq-card"><div class="rq-card-label">Learn More</div>' + learnHTML + '</div>';

      /* Resources */
      el('rqResources').innerHTML = arch.resources.map(function (r) {
        return '<a href="' + r.href + '" target="_blank" class="rq-resource-chip">'
          + '<span>' + r.icon + '</span>' + r.text + '</a>';
      }).join('');
    }

    /* ── Retake ── */
    function retake() {
      state = { step: 1, s1: [], s2: [], s3: null };
      el('rqResults').className = 'rq-results';
      el('rqResults').style.display = 'none';
      el('rqQuiz').style.display    = 'block';
      render();
      el('rqQuiz').scrollIntoView({ behavior: 'smooth' });
    }

    /* ── Event delegation on the section root ── */
    function attachEvents() {
      var sectionRoot = root();
      if (!sectionRoot) return;

      sectionRoot.addEventListener('click', function (e) {
        /* Option chip or goal card */
        var optBtn = e.target.closest('[data-rq-step][data-rq-idx]');
        if (optBtn) {
          selectOption(
            parseInt(optBtn.getAttribute('data-rq-step'), 10),
            parseInt(optBtn.getAttribute('data-rq-idx'), 10)
          );
          return;
        }

        /* Back button */
        if (e.target.closest('#rqBack-' + SID)) {
          state.step--;
          render();
          return;
        }

        /* Next / submit */
        if (e.target.closest('#rqNext-' + SID)) {
          if (state.step < 3) {
            state.step++;
            render();
          } else {
            showResults();
          }
          return;
        }
      });
    }

    /* ── Init ── */
    function init() {
      if (!root()) return;
      attachEvents();
      /* Wire the static retake button in the liquid results HTML */
      var retakeBtn = el('rqRetake');
      if (retakeBtn) retakeBtn.addEventListener('click', retake);
      render();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  /* ═══════════════════════════════════════════════════════════════════
     BOOT — initialise every quiz instance found on the page
  ═══════════════════════════════════════════════════════════════════ */
  function bootAll() {
    var instances = window.RYGQuizInstances || {};
    Object.keys(instances).forEach(function (sectionId) {
      RYGQuiz(instances[sectionId]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

})();